import React, { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Plus, Search, Loader2, Package, Layers, Settings, Trash2, Link as LinkIcon, X, Image as ImageIcon, GripVertical, AlertCircle, CheckCircle2 } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";

// Option row within a modifier group
function OptionRow({ idx, option, updateOption, removeOption, optionsLength, authAxios }) {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);

  const uploadImage = async (file) => {
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await authAxios.post("/menu/upload-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateOption(idx, "image_url", res.data.url);
    } catch { toast.error("Error al subir imagen"); }
    setUploading(false);
  };

  const resolveUrl = (url) => url && url.startsWith("/api/") ? `${API_URL}${url}` : url;

  return (
    <div className="bg-gray-50 p-3 rounded-lg space-y-2 border border-gray-100" data-testid={`option-row-${idx}`}>
      <div className="flex items-center gap-2">
        <GripVertical className="w-4 h-4 text-gray-300 cursor-move shrink-0" />
        <div 
          className="w-10 h-10 rounded bg-gray-200 shrink-0 overflow-hidden cursor-pointer relative hover:ring-2 hover:ring-emerald-300 transition" 
          onClick={() => fileRef.current?.click()}
        >
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadImage(e.target.files[0])} />
          {uploading ? <Loader2 className="w-4 h-4 animate-spin absolute inset-0 m-auto text-gray-500" /> :
           option.image_url ? <img src={resolveUrl(option.image_url)} alt="" className="w-full h-full object-cover" /> :
           <ImageIcon className="w-4 h-4 absolute inset-0 m-auto text-gray-400" />}
        </div>
        <Input 
          value={option.name || ""} 
          onChange={e => updateOption(idx, "name", e.target.value)} 
          placeholder="Nombre de la opcion (ej: Salmon, Grande, Teriyaki...)" 
          className="flex-1" 
          data-testid={`option-name-${idx}`} 
        />
        <div className="flex items-center gap-1 w-28">
          <span className="text-sm text-gray-400">+</span>
          <Input 
            type="number" 
            step="0.01" 
            value={option.price || ""} 
            onChange={e => updateOption(idx, "price", e.target.value)} 
            placeholder="0.00" 
            className="w-full" 
            data-testid={`option-price-${idx}`} 
          />
          <span className="text-sm text-gray-500">EUR</span>
        </div>
        {optionsLength > 1 && (
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => removeOption(idx)}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Main component for managing modifier groups
export function VariacionesTab({ products }) {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [assignModal, setAssignModal] = useState(null);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [required, setRequired] = useState(true);
  const [minSelect, setMinSelect] = useState(1);
  const [maxSelect, setMaxSelect] = useState(1);
  const [options, setOptions] = useState([{ name: "", price: "", image_url: "" }]);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/menu/modifier-groups");
      setGroups(res.data);
    } catch (err) {
      console.error("Error fetching modifier groups:", err);
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const resetForm = () => { 
    setName(""); 
    setDescription("");
    setRequired(true); 
    setMinSelect(1); 
    setMaxSelect(1); 
    setOptions([{ name: "", price: "", image_url: "" }]); 
    setEditingGroup(null); 
  };

  const openCreate = () => { resetForm(); setShowForm(true); };
  
  const openEdit = (group) => {
    setEditingGroup(group);
    setName(group.name);
    setDescription(group.description || "");
    setRequired(group.required !== false);
    setMinSelect(group.min_select || 1);
    setMaxSelect(group.max_select || 1);
    setOptions(group.options?.length > 0 ? group.options.map(o => ({
      id: o.id,
      name: o.name,
      price: o.price || "",
      image_url: o.image_url || "",
      description: o.description || ""
    })) : [{ name: "", price: "", image_url: "" }]);
    setShowForm(true);
  };

  const addOption = () => setOptions([...options, { name: "", price: "", image_url: "" }]);
  const removeOption = (idx) => setOptions(options.filter((_, i) => i !== idx));
  const updateOption = (idx, field, value) => setOptions(options.map((opt, i) => i === idx ? { ...opt, [field]: value } : opt));

  const saveGroup = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nombre del grupo es obligatorio"); return; }
    
    const validOptions = options.filter(o => o.name.trim()).map((o, i) => ({ 
      id: o.id || undefined,
      name: o.name.trim(), 
      price: parseFloat(o.price) || 0, 
      image_url: o.image_url || "", 
      description: o.description || "",
      position: i
    }));
    
    if (validOptions.length === 0) { 
      toast.error("Agrega al menos una opcion al grupo"); 
      return; 
    }

    // Validate min/max logic
    if (minSelect > maxSelect) {
      toast.error("Minimo no puede ser mayor que maximo");
      return;
    }
    if (required && minSelect < 1) {
      toast.error("Si es obligatorio, minimo debe ser al menos 1");
      return;
    }
    if (maxSelect > validOptions.length) {
      toast.error(`Maximo (${maxSelect}) no puede ser mayor que el numero de opciones (${validOptions.length})`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = { 
        name: name.trim(), 
        description: description.trim(),
        required, 
        min_select: minSelect, 
        max_select: maxSelect, 
        options: validOptions 
      };
      
      if (editingGroup) { 
        await authAxios.put(`/menu/modifier-groups/${editingGroup.id}`, payload); 
        toast.success("Grupo de modificadores actualizado"); 
      } else { 
        await authAxios.post("/menu/modifier-groups", payload); 
        toast.success("Grupo de modificadores creado"); 
      }
      setShowForm(false); 
      resetForm(); 
      fetchGroups();
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Error al guardar"); 
    }
    setSubmitting(false);
  };

  const deleteGroup = async (id) => { 
    if (!window.confirm("Eliminar este grupo? Se removera de todos los productos.")) return;
    try { 
      await authAxios.delete(`/menu/modifier-groups/${id}`); 
      toast.success("Grupo eliminado"); 
      fetchGroups(); 
    } catch { 
      toast.error("Error al eliminar"); 
    } 
  };

  const assignToProduct = async (productId, groupId) => { 
    try { 
      await authAxios.post(`/menu/products/${productId}/modifier-groups`, { group_id: groupId }); 
      toast.success("Grupo asignado al producto"); 
      setAssignModal(null); 
    } catch { 
      toast.error("Error al asignar"); 
    } 
  };

  const getLinkedProducts = (groupId) => products.filter(p => (p.modifier_group_ids || []).includes(groupId));
  const filtered = groups.filter(g => !search || g.name.toLowerCase().includes(search.toLowerCase()));

  // Helper for selection type label
  const getSelectionLabel = (group) => {
    if (group.min_select === group.max_select) {
      return group.min_select === 1 ? "Elegir 1" : `Elegir ${group.min_select}`;
    }
    if (group.min_select === 0) {
      return `Hasta ${group.max_select}`;
    }
    return `${group.min_select}-${group.max_select}`;
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  return (
    <div data-testid="variaciones-tab">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Buscar grupo de modificadores..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
            className="pl-9 bg-white border-gray-200" 
            data-testid="search-modifiers" 
          />
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={openCreate} data-testid="add-modifier-group-btn">
          <Plus className="w-4 h-4" /> Nuevo Grupo
        </Button>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-blue-700">
          <strong>Grupos de Modificadores</strong> permiten personalizar productos (ej: elegir proteina, ingredientes, tamano, extras). 
          Crea grupos y asignalos a productos para que los clientes puedan elegir al pedir.
        </p>
      </div>

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400 bg-white rounded-lg border border-gray-200">
          <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium text-gray-600">Sin grupos de modificadores</p>
          <p className="text-sm mt-1">Crea grupos como "Proteina", "Tamano", "Extras" o "Molhos"</p>
          <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" /> Crear Primer Grupo
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(group => {
            const linked = getLinkedProducts(group.id);
            return (
              <div key={group.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition" data-testid={`modifier-group-${group.id.slice(-6)}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-emerald-600" />
                    <span className="font-bold text-gray-900 text-lg">{group.name}</span>
                    {group.required && (
                      <Badge variant="outline" className="text-xs border-orange-300 text-orange-600 bg-orange-50">
                        Obligatorio
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs bg-gray-50">
                      {getSelectionLabel(group)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="text-blue-600 h-8 hover:bg-blue-50" onClick={() => setAssignModal(group)} data-testid={`assign-group-${group.id.slice(-6)}`}>
                      <LinkIcon className="w-4 h-4 mr-1" /> Asignar
                    </Button>
                    <Button variant="ghost" size="sm" className="text-gray-500 h-8 hover:bg-gray-100" onClick={() => openEdit(group)}>
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-400 h-8 hover:bg-red-50 hover:text-red-600" onClick={() => deleteGroup(group.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {group.description && (
                  <p className="text-sm text-gray-500 mb-2">{group.description}</p>
                )}
                
                {/* Options list */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {(group.options || []).slice(0, 8).map((opt, i) => (
                    <span key={i} className="bg-gray-100 text-gray-700 text-sm px-3 py-1.5 rounded-full flex items-center gap-1.5">
                      {opt.image_url && <img src={opt.image_url.startsWith("/api/") ? `${API_URL}${opt.image_url}` : opt.image_url} alt="" className="w-5 h-5 rounded-full object-cover" />}
                      {opt.name}
                      {opt.price > 0 && <span className="text-emerald-600 font-medium">+{opt.price.toFixed(2)}EUR</span>}
                    </span>
                  ))}
                  {(group.options || []).length > 8 && (
                    <span className="text-gray-400 text-sm px-2 py-1">+{group.options.length - 8} mas</span>
                  )}
                </div>
                
                {/* Linked products */}
                {linked.length > 0 ? (
                  <div className="text-xs text-gray-500 flex items-center gap-1 bg-gray-50 rounded px-2 py-1">
                    <Package className="w-3 h-3" /> 
                    <span className="font-medium">Productos:</span> {linked.slice(0, 5).map(p => p.name).join(", ")}
                    {linked.length > 5 && <span> +{linked.length - 5} mas</span>}
                  </div>
                ) : (
                  <div className="text-xs text-amber-600 flex items-center gap-1 bg-amber-50 rounded px-2 py-1">
                    <AlertCircle className="w-3 h-3" /> Sin productos asignados
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); resetForm(); } else setShowForm(true); }}>
        <DialogContent className="max-w-xl max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="uppercase font-bold text-lg">
              {editingGroup ? "Editar Grupo de Modificadores" : "Nuevo Grupo de Modificadores"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveGroup} className="flex flex-col flex-1 overflow-hidden">
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-1 space-y-4 mt-2">
            {/* Group name */}
            <div>
              <Label className="font-semibold">Nombre del Grupo <span className="text-red-500">*</span></Label>
              <Input 
                value={name} 
                onChange={e => setName(e.target.value)} 
                placeholder='Ej: "Proteina", "Tamano", "Extras", "Molho"' 
                className="mt-1" 
                data-testid="group-name" 
              />
              <p className="text-xs text-gray-400 mt-1">Este nombre aparecera al cliente cuando personalice el producto</p>
            </div>
            
            {/* Description */}
            <div>
              <Label className="text-sm text-gray-600">Descripcion (opcional)</Label>
              <Input 
                value={description} 
                onChange={e => setDescription(e.target.value)} 
                placeholder='Ej: "Elija su proteina favorita"' 
                className="mt-1" 
                data-testid="group-description" 
              />
            </div>
            
            {/* Rules */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <Label className="font-semibold text-gray-700">Reglas de Seleccion</Label>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Switch checked={required} onCheckedChange={setRequired} data-testid="group-required" />
                  <Label className="text-sm cursor-pointer" onClick={() => setRequired(!required)}>Obligatorio</Label>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Minimo</Label>
                  <Input 
                    type="number" 
                    min={0} 
                    value={minSelect} 
                    onChange={e => setMinSelect(parseInt(e.target.value) || 0)} 
                    className="mt-1" 
                    data-testid="group-min"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Maximo</Label>
                  <Input 
                    type="number" 
                    min={1} 
                    value={maxSelect} 
                    onChange={e => setMaxSelect(parseInt(e.target.value) || 1)} 
                    className="mt-1" 
                    data-testid="group-max"
                  />
                </div>
              </div>
              {/* Rules explanation */}
              <div className="text-xs text-gray-500 bg-white rounded p-2 border">
                {required ? (
                  minSelect === maxSelect ? (
                    <span>Cliente <strong>debe elegir exactamente {minSelect}</strong> opcion(es)</span>
                  ) : (
                    <span>Cliente <strong>debe elegir entre {minSelect} y {maxSelect}</strong> opcion(es)</span>
                  )
                ) : (
                  <span>Cliente <strong>puede elegir hasta {maxSelect}</strong> opcion(es) (opcional)</span>
                )}
              </div>
            </div>
            
            {/* Options */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Opciones del Grupo <span className="text-red-500">*</span></Label>
                <Button type="button" variant="outline" size="sm" onClick={addOption} className="gap-1 h-8 text-xs">
                  <Plus className="w-3 h-3" /> Agregar Opcion
                </Button>
              </div>
              <div className="space-y-2">
                {options.map((opt, idx) => (
                  <OptionRow 
                    key={idx} 
                    idx={idx} 
                    option={opt} 
                    updateOption={updateOption} 
                    removeOption={removeOption} 
                    optionsLength={options.length} 
                    authAxios={authAxios} 
                  />
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">Precio adicional = 0 significa sin costo extra</p>
            </div>
            </div>
            
            {/* Fixed footer with buttons */}
            <div className="shrink-0 pt-4 mt-4 border-t bg-white flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-group-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editingGroup ? "Actualizar" : "Crear Grupo"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign to Product Dialog */}
      <Dialog open={!!assignModal} onOpenChange={(o) => { if (!o) setAssignModal(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold">Asignar "{assignModal?.name}" a Producto</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">Selecciona los productos que usaran este grupo de modificadores:</p>
          <ScrollArea className="max-h-80 border rounded-lg mt-2">
            <div className="p-2 space-y-1">
              {products.filter(p => p.for_sale).map(p => {
                const isLinked = (p.modifier_group_ids || []).includes(assignModal?.id);
                return (
                  <button 
                    key={p.id} 
                    className={`w-full text-left px-3 py-2.5 text-sm rounded-lg flex items-center justify-between transition ${isLinked ? "bg-emerald-50 border border-emerald-200" : "hover:bg-gray-50 border border-transparent"}`} 
                    onClick={() => !isLinked && assignToProduct(p.id, assignModal?.id)} 
                    data-testid={`assign-product-${p.id.slice(-6)}`}
                  >
                    <span className={isLinked ? "text-emerald-700 font-medium" : "text-gray-700"}>{p.name}</span>
                    {isLinked ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Asignado
                      </Badge>
                    ) : (
                      <span className="text-xs text-gray-400">{p.price?.toFixed(2) || "0.00"}EUR</span>
                    )}
                  </button>
                );
              })}
              {products.filter(p => p.for_sale).length === 0 && (
                <p className="text-center text-gray-400 py-4">No hay productos disponibles</p>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModal(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
